import { Accessor, For, Show, createSignal, createEffect, createMemo } from 'solid-js';
import { Select } from '../../select/select';
import {
    StyledTable,
    StyledLeftmostTableHeader,
    StyledLeftmostTableCell,
    StyledTableRow,
    StyledTableHeader,
    StyledTableCell,
    StyledConditionsOfUseTableContainer,
    StyledPlaceHolder,
    StyledConditionsOfUse,
    StyledConditionsOfUseTableHeader,
    StyledSource,
    StyledSourceDecision,
} from './conditions-of-use.styles';

import { formatRows } from './helpers';
import { ChemicalInspectionTableData } from '../product-types';
import { StyledLink } from '../../strategies/strategies.styles';
import { ArrowOut } from '../../svg/svg';

type ConditionsOfUseProps = {
    tableData: ChemicalInspectionTableData;
    pdfLink?: string;
    kiProductUrl?: string;
    decisionType?: string;
    decisionDate?: string;
};

export const ConditionsOfUse = (props: ConditionsOfUseProps) => {
    const [selectedSituations, setSelectedSituations] = createSignal<string[]>([]);

    const usageTableRows = () => formatRows(props.tableData.rows);

    // Where the table below actually came from. KI publishes the conditions as
    // structured data under "Användning" on its own product page, and for those
    // products that page is the source, not the bilaga. Products still read from
    // the bilaga keep the PDF link they have always had.
    const source = createMemo(() => {
        if (props.kiProductUrl) {
            return {
                href: props.kiProductUrl,
                text: 'Kemikalieinspektionens produktsida',
            };
        }

        if (props.pdfLink) {
            return {
                href: props.pdfLink,
                text: 'Kemikalieinspektionen-PDF',
            };
        }

        return null;
    });

    // Which KI decision the table comes from, named as KI names it. Products
    // synced before this existed have neither field and show no line at all.
    const decision = createMemo(() => {
        const parts: string[] = [];

        if (props.decisionType) {
            parts.push(props.decisionType);
        }

        if (props.decisionDate) {
            parts.push(`gäller från ${props.decisionDate}`);
        }

        if (parts.length === 0) {
            return null;
        }

        return `Beslut: ${parts.join(', ')}`;
    });

    const getSituationSelectOptions = createMemo(() => {
        const situations = props.tableData.rows.map((column: string[]) => column[0].trim());

        const groupedSituations = situations.reduce((acc, situation, index) => {
            acc[situation] = acc[situation] || [];
            acc[situation].push(index + 1);
            return acc;
        }, {} as Record<string, number[]>);

        return Object.entries(groupedSituations).map(([text, indices]) => ({
            value: indices.join(','),
            text,
        }));
    });

    createEffect(() => {
        if (getSituationSelectOptions().length === 1) {
            // If there's only one option, set it as selected
            const valuesOfOnlyOption = getSituationSelectOptions().map((option) => option.value);
            const allIndices = valuesOfOnlyOption.flatMap((value) => value.split(','));
            setSelectedSituations(allIndices);
        }
    });

    const rowsWithSelectedColumns = () => {
        const headerAndRows = usageTableRows().map((row: string[], index: number) => {
            return [props.tableData.header[index], ...row];
        });

        const indicesToKeep = selectedSituations().map((index) => parseInt(index, 10));
        indicesToKeep.unshift(0);

        const selected = headerAndRows.map((row) => indicesToKeep.map((index) => row[index]));
        const columns = selected.slice(0, selected.length - 1);

        return columns.map((column) => column.map((c) => c.replace(/\s/g, ' ')));
    };

    return (
        <div>
            <StyledConditionsOfUseTableHeader>
                <p>
                    <strong>{props.tableData.title}</strong>
                </p>
                <StyledSource>
                    <Show when={source()}>
                        <p>
                            Källa:{' '}
                            <StyledLink href={source()?.href as string} target="_blank">
                                {source()?.text}
                                <ArrowOut />
                            </StyledLink>
                        </p>
                    </Show>
                    <Show when={decision()}>
                        <StyledSourceDecision>{decision()}</StyledSourceDecision>
                    </Show>
                </StyledSource>
            </StyledConditionsOfUseTableHeader>
            <Select
                mode="multiple"
                mergeDuplicates={true}
                value={selectedSituations()}
                options={getSituationSelectOptions()}
                placeholder="Välj gröda..."
                onChange={(selectedValues: string[]) => {
                    const allIndices = selectedValues.flatMap((value) => value.split(','));
                    setSelectedSituations([...new Set(allIndices)]);
                }}
            />
            <StyledConditionsOfUse selectedSituationsAmount={selectedSituations().length}>
                <StyledConditionsOfUseTableContainer>
                    <StyledTable fullWidth={selectedSituations().length > 0}>
                        <thead>
                            <tr>
                                <For each={rowsWithSelectedColumns()[0]}>
                                    {(column: string, index: Accessor<number>) =>
                                        index() === 0 ? (
                                            <StyledLeftmostTableHeader>{column}</StyledLeftmostTableHeader>
                                        ) : (
                                            <StyledTableHeader selectedSituationsAmount={selectedSituations().length}>
                                                {column}
                                            </StyledTableHeader>
                                        )
                                    }
                                </For>
                            </tr>
                        </thead>
                        <tbody>
                            <For each={rowsWithSelectedColumns().slice(1)}>
                                {(row: string[]) => (
                                    <StyledTableRow>
                                        <For each={row}>
                                            {(column: string, index: Accessor<number>) =>
                                                index() === 0 ? (
                                                    <StyledLeftmostTableCell>{column}</StyledLeftmostTableCell>
                                                ) : (
                                                    <StyledTableCell selectedSituationsAmount={selectedSituations().length}>
                                                        {column}
                                                    </StyledTableCell>
                                                )
                                            }
                                        </For>
                                    </StyledTableRow>
                                )}
                            </For>
                        </tbody>
                    </StyledTable>
                    <Show when={selectedSituations().length < 1}>
                        <StyledPlaceHolder>
                            <h3>Välj gröda</h3>
                            <p>Välj en eller flera grödor ovanför för att se dess villkor för användning.</p>
                        </StyledPlaceHolder>
                    </Show>
                </StyledConditionsOfUseTableContainer>
            </StyledConditionsOfUse>
        </div>
    );
};
